import type { LogRecord, LogSink } from './types';

/**
 * Sentry sink — stubbed, not wired.
 *
 * Why a stub instead of the real SDK?
 *   We want the logger's call sites ("await logger.error(...)") to be
 *   identical in every environment, and we want CI + type-checking to
 *   exercise the integration path today so that dropping in
 *   `@sentry/react-native` or `@sentry/nextjs` in a later PR is a
 *   near-trivial code change. Installing the vendor SDK now would add
 *   native modules to Expo prebuild and bundle weight before we even
 *   have a real backend — no deal.
 *
 * The stub forwards `warn` and `error` records to an injected adapter.
 * `debug` and `info` are dropped on the floor. When the real Sentry
 * SDK lands, the app wiring becomes:
 *
 *     import * as Sentry from '@sentry/react-native';
 *     configureLogger({
 *       level: 'info',
 *       sinks: [
 *         consoleSink,
 *         sentrySink({
 *           captureMessage: (msg, opts) => Sentry.captureMessage(msg, opts),
 *           captureException: (err, opts) => Sentry.captureException(err, opts),
 *         }),
 *       ],
 *     });
 *
 * The scrubber in the top-level logger has already stripped PII by the
 * time a record reaches this sink, so we can hand the context to
 * Sentry's `extra` field without further processing.
 */

export interface SentryAdapterOptions {
  readonly level: 'warning' | 'error';
  readonly extra?: Record<string, unknown>;
  readonly tags?: Record<string, string>;
}

export interface SentryAdapter {
  captureMessage(message: string, options: SentryAdapterOptions): void;
  captureException(error: unknown, options: SentryAdapterOptions): void;
}

export interface SentrySinkOptions {
  /** Where to send the captured records. Inject the real SDK here. */
  readonly adapter: SentryAdapter;
  /** Minimum severity forwarded to Sentry. Defaults to `warn`. */
  readonly minLevel?: 'warn' | 'error';
  /** Static tags attached to every event (e.g. `{ app: 'mobile' }`). */
  readonly tags?: Record<string, string>;
}

export function sentrySink(options: SentrySinkOptions): LogSink {
  const minLevel = options.minLevel ?? 'warn';
  return {
    write(record: LogRecord): void {
      if (record.level === 'debug') return;
      if (record.level === 'info') return;
      if (minLevel === 'error' && record.level === 'warn') return;

      const severity: SentryAdapterOptions['level'] =
        record.level === 'error' ? 'error' : 'warning';
      const payload: SentryAdapterOptions = {
        level: severity,
        extra: record.context as Record<string, unknown> | undefined,
        tags: options.tags,
      };

      try {
        if (record.error !== undefined) {
          options.adapter.captureException(record.error, payload);
          return;
        }
        options.adapter.captureMessage(record.message, payload);
      } catch {
        // Never let a failing telemetry backend crash the call site.
      }
    },
  };
}

/**
 * Dev-time adapter that just collects events in memory. Handy for
 * tests and for confirming the wiring works before the real SDK is
 * installed.
 */
export interface CapturedEvent {
  readonly kind: 'message' | 'exception';
  readonly payload: unknown;
  readonly options: SentryAdapterOptions;
}

export function inMemorySentryAdapter(): SentryAdapter & {
  readonly events: readonly CapturedEvent[];
  reset(): void;
} {
  const events: CapturedEvent[] = [];
  return {
    events,
    captureMessage(message, options) {
      events.push({ kind: 'message', payload: message, options });
    },
    captureException(error, options) {
      events.push({ kind: 'exception', payload: error, options });
    },
    reset() {
      events.length = 0;
    },
  };
}
